import type { FlagKey } from '@doneverse/shared'
import { hasNativeCalls } from '@/lib/mobile-bridge'
import { EMPTY_FLAGS, useDashboardStore } from '@/store'

/**
 * Flags forced on inside the native mobile shell, on top of whatever the server
 * resolved. NOT dev-only — this is how the iOS beta gets calls without a
 * per-tester override in `feature_flag_user_overrides`.
 *
 * Gated on `hasNativeCalls()` rather than `isMobileBridge()`: the shell only
 * sets that once CallKit/PushKit registration succeeded, and without it an
 * incoming call can't ring and the in-WebView fallback path is unproven on
 * WKWebView — so a Call button there would be a broken affordance.
 *
 * The trade-off, deliberately accepted: this is "the app", not "TestFlight".
 * Any native install qualifies — Android, sideloads, and an App Store build if
 * one ships — and turning it back off needs a dashboard deploy, not a flag
 * flip. Revisit if a client dimension lands on `feature_flag_rules`.
 */
const NATIVE_SHELL_FLAGS: readonly FlagKey[] = ['huddles']

/**
 * LOCAL/E2E-ONLY flag overrides — everything below `NATIVE_SHELL_FLAGS`. In
 * production builds every function here returns the same shared EMPTY value;
 * server-resolved flags from `/v1/me` are the authoritative source. The
 * explicit E2E build keeps URL/store overrides available to Playwright while
 * still excluding a developer's optional local override file.
 *
 * Three layers (all dev-only, all union/override on top of server flags):
 *
 *   • Optional `feature-flags.local.ts` next to this file (gitignored, typed).
 *     Default export is `Partial<Record<FlagKey, boolean>>` — `true` forces a
 *     flag on, `false` forces it off, missing keys fall through to the server.
 *   • `?flags=drive-sync,other-flag` in the URL is merged into the dashboard
 *     store on boot, then the query param is stripped.
 *   • The "Dev flag overrides" banner exposes a Clear button for the store.
 */

const isE2E = (): boolean => import.meta.env.VITE_E2E_TEST_MODE === 'true'
const isDevOrE2E = (): boolean => import.meta.env.DEV || isE2E()

export type LocalFileFlags = Partial<Record<FlagKey, boolean>>

// Eager glob: if `feature-flags.local.ts` exists Vite inlines it; otherwise
// the map is empty and tree-shakes away. Keeping the file optional avoids
// every contributor needing to create it.
const localFileModules = import.meta.glob<{ default: LocalFileFlags }>('./feature-flags.local.ts', {
  eager: true,
})
const localFileOverrides: LocalFileFlags = Object.values(localFileModules)[0]?.default ?? {}

const EMPTY_OVERRIDES: LocalFileFlags = Object.freeze({})

export function getLocalFileOverrides(): LocalFileFlags {
  return import.meta.env.DEV && !isE2E() ? localFileOverrides : EMPTY_OVERRIDES
}

export function getFlags(): readonly string[] {
  if (!isDevOrE2E()) return EMPTY_FLAGS
  return useDashboardStore.getState().devFlagOverrides
}

/**
 * Resolve the enabled flags for a profile: server flags, then
 * `NATIVE_SHELL_FLAGS`, then the three dev-only layers above. The local file
 * runs last so a dev can still force a native-shell flag back off.
 *
 * The body of `useFeatureFlags`, lifted out so non-React callers resolve flags
 * the same way the UI does. The router's `beforeLoad` guards are the reason
 * this exists: they have a profile but no hooks, and a second resolution order
 * there would silently disagree with what the user actually sees.
 *
 * `devOverrides` is a parameter rather than a `getFlags()` call so the hook can
 * pass its store-subscribed value and stay reactive.
 */
export function resolveEnabledFlags(
  serverFlags: Record<string, boolean> | undefined,
  devOverrides: readonly string[] = getFlags(),
): FlagKey[] {
  const result = new Set<FlagKey>()
  for (const [key, enabled] of Object.entries(serverFlags ?? {})) {
    if (enabled) result.add(key as FlagKey)
  }
  if (hasNativeCalls()) {
    for (const key of NATIVE_SHELL_FLAGS) result.add(key)
  }
  for (const key of devOverrides) result.add(key as FlagKey)
  for (const [key, enabled] of Object.entries(getLocalFileOverrides())) {
    if (enabled) result.add(key as FlagKey)
    else result.delete(key as FlagKey)
  }
  return Array.from(result)
}

export function setFlags(flags: readonly string[]): void {
  if (!isDevOrE2E()) return
  useDashboardStore.getState().setDevFlagOverrides(flags)
}

export function hasFlag(flag: FlagKey): boolean {
  return getFlags().includes(flag)
}

export function clearFlags(): void {
  if (!isDevOrE2E()) return
  useDashboardStore.getState().clearDevFlagOverrides()
}

/**
 * Parse `?flags=a,b` from the current URL, merge into the store, then strip
 * the param. Call once at module scope before React mounts. No-op in
 * production.
 */
export function syncFlagsFromUrl(): void {
  if (!isDevOrE2E()) return
  const url = new URL(window.location.href)
  const param = url.searchParams.get('flags')
  if (!param) return

  const incoming = param
    .split(',')
    .map((f) => f.trim())
    .filter(Boolean)
  if (incoming.length === 0) return

  const merged = Array.from(new Set([...getFlags(), ...incoming]))
  setFlags(merged)

  url.searchParams.delete('flags')
  window.history.replaceState(null, '', url.pathname + url.search + url.hash)
}
